import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function FAQ() {
  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="section-container">
          <span className="section-label">FAQ</span>
          <h1 className="page-title">Questions fréquentes</h1>
          <p className="page-subtitle">
            Des réponses claires aux questions essentielles sur notre approche, notre méthode et nos interventions.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <Accordion type="single" collapsible className="faq-accordion">
            <AccordionItem value="item-1">
              <AccordionTrigger>Quel est le rôle d'Open Cortex dans une organisation ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Open Cortex intervient sur les fondations opérationnelles des organisations : compréhension du fonctionnement réel, structuration des processus, mise en place de leviers d'automatisation ciblés et intégration raisonnée de l'intelligence artificielle.
                </p>
                <p>
                  Nous intervenons en amont des décisions techniques pour clarifier les besoins, hiérarchiser les priorités et concevoir des solutions adaptées au contexte. Notre rôle est de donner aux dirigeants les moyens de piloter leur évolution opérationnelle avec clarté et continuité.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>Quelle place occupent l'automatisation et l'IA dans vos interventions ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  L'automatisation et l'intelligence artificielle sont des leviers puissants lorsqu'ils s'appuient sur des processus structurés et des besoins clairement identifiés. Nous les intégrons de manière ciblée, après avoir clarifié le fonctionnement existant et déterminé où leur usage apporte une valeur mesurable.
                </p>
                <p>
                  Concrètement, cela concerne la coordination opérationnelle, l'automatisation de workflows, la qualification de données, l'aide à la décision ou encore la continuité en cas d'absence. L'objectif : fluidifier les opérations, libérer du temps décisionnel et renforcer la capacité à traiter des volumes croissants.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>Comment se déroule une mission avec Open Cortex ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Chaque mission suit une méthode structurée en quatre phases : Diagnostic, Conception, Formalisation et Transfert. Ce cadre méthodologique s'ajuste à chaque contexte : le périmètre d'intervention, les étapes activées et les livrables associés sont précisés dans le devis.
                </p>
                <p>
                  Le déroulement repose sur des échanges réguliers, une capacité à ajuster le cap en fonction des retours terrain et un transfert systématique des compétences pour garantir l'autonomie opérationnelle à l'issue de la mission.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>Quels sont les livrables d'une intervention ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Les livrables dépendent du périmètre défini dans le devis. Ils peuvent inclure :
                </p>
                <ul>
                  <li>Une cartographie des processus clés et une identification des points d'attention prioritaires</li>
                  <li>Des spécifications fonctionnelles et des schémas de workflows automatisés</li>
                  <li>Une documentation complète et exploitable au quotidien</li>
                  <li>Des solutions d'automatisation ou d'IA intégrées à vos opérations</li>
                  <li>Un transfert de compétences permettant de maintenir et faire évoluer ce qui a été mis en place</li>
                </ul>
                <p>
                  Tous les livrables produits dans le cadre d'une mission vous appartiennent intégralement. Vous êtes libre de les utiliser, de les modifier, de les transmettre ou de les partager avec d'autres prestataires.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>Quel est le cadre de collaboration ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Open Cortex intervient selon une méthode structurée, mobilisant expertise, rigueur et expérience du terrain. Chaque mission repose sur un périmètre clairement défini et des livrables explicites.
                </p>
                <p>
                  Avant tout engagement, un devis détaillé formalise le cadre d'intervention : objectifs, étapes, livrables attendus et modalités pratiques. Cette clarté initiale permet un travail serein et des échanges réguliers tout au long de la mission. Les interventions démarrent à partir de 2 500 € HT.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>Avec quels types d'organisations travaillez-vous ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Nous intervenons principalement auprès d'organisations ayant des besoins de structuration opérationnelle, d'automatisation ciblée ou d'intégration raisonnée de l'intelligence artificielle. Cela concerne des PME, des organisations de taille intermédiaire et des structures en croissance ayant besoin de clarifier leur fonctionnement et de le rendre scalable.
                </p>
                <p>
                  Si votre besoin porte sur un développement logiciel complexe ou une transformation stratégique de grande envergure, nous vous orienterons vers des partenaires mieux positionnés. Le premier échange permet de clarifier cette adéquation.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger>Comment démarrer une collaboration ?</AccordionTrigger>
              <AccordionContent>
                <p>
                  Le premier contact est un échange pour comprendre votre contexte, vos priorités et vos besoins opérationnels. Nous discutons de votre situation actuelle, des leviers que vous envisagez et de la pertinence d'une intervention d'Open Cortex.
                </p>
                <p>
                  Si notre approche correspond à vos attentes, nous établissons un devis détaillé précisant le périmètre, les livrables, les étapes et les modalités de collaboration. Vous décidez ensuite si vous souhaitez poursuivre.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Une question spécifique ?</h2>
            <p>Contactez-nous pour en discuter directement.</p>
            <Link to="/contact">
              <Button className="btn-primary btn-large">
                Prendre contact <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
